// owner-map: OP-04a.spa
// Reforecast command (EPIC #157, phase P5.3 — design §2.2).
//
// `reforecast` moved VERBATIM out of `hooks/useReforecast.js` — resubmits the active
// forecast's question through the same orchestrator, reruns every branch in parallel, and
// merges each fresh result onto the old (preserving past events). The only behavioral
// rewrites are the migration-mandated ones:
//   • `runStateRef.current`    → `deps.store.getState()`   (retires §7 #1)
//   • `execByRunIdRef.current` → the `execTable` module singleton (§7 #3 claim/release)
//   • I/O (`genForecastFromServer` / `streamBranch`) through `deps.services.forecastClient`
//   • `dispatchRun` / tour globals threaded via deps (§7 #8)
//
// §7 invariant preserved byte-for-byte: reforecast is DELIBERATELY NON-ABORTABLE — every
// exec claim uses `abortController: null` and every stream call passes `signal: null`. The
// scrub (`useReforecastScrub`) stays a hook — it's UI state, not part of this op.
import { buildReforecastPreprocessed } from "../lib/forecast/reforecastRequest.js";
import { applyNarrationFrame } from "../lib/forecast/narrationRouter.js";
import { applyUpdateFrame } from "../lib/forecast/updateRouter.js";
import { mergeReforecastEvents } from "../lib/forecast/reforecastEvents.js";
import { liveEventsReconcileInPlace, projectLiveEvents } from "../lib/forecast/liveTriggers.js";
import { outcomeSpaceOfRun, withOutcomeSpace } from "../lib/forecast/outcomeSpace.js";
import {
  branchDirContext,
  occurAssumption,
  notOccurAssumption,
  customEventAssumption,
} from "../lib/forecast/branchAssumption.js";
import {
  runToLegacyForecast,
  selectBranchRuns,
  selectPremiseRunsById,
  selectRun,
  selectRunTree,
  selectUnarchivedBranchRuns,
} from "../lib/forecast/selectors.js";
import {
  isTerminalPremiseState,
  nextPremiseRecord,
  premiseAttemptWarranted,
  premiseViewsFor,
} from "../lib/forecast/branchPremise.js";
import { sameSourceTerms } from "../lib/forecast/derivedBranch.js";
import { occurrenceKeyOf } from "../lib/forecast/eventIdentity.js";
import { canUpdateNow } from "../lib/forecast/sharedRowGuards.js";
import { lacksDetail } from "../lib/forecast/detailBackfill.js";
import { stripDebugForWire } from "../lib/forecast/contentSave.js";
import { persistForecast } from "./persistForecast.js";
import { writeSharedRerunHandle } from "../lib/forecast/storageKeys.js";
import { settleSharedRerunHandle } from "./resumeForecasts.js";
import {
  runStarted,
  runProgress,
  runReforecasted,
  runCompleted,
  runErrored,
  contractKeySet,
  traceReallocated,
  premiseCleared,
  premiseOutcomesMarked,
} from "../lib/forecast/reducer.js";
import { progressPayload, startProgressIntro } from "../lib/forecast/progress.js";
import { entryAppended } from "../lib/forecast/reducer.js";
import { makeEntry } from "../lib/forecast/model.js";
import { summaryConvId } from "../lib/forecast/summaryPost.js";
import { entryToMessageBody } from "../lib/forecast/hydrate.js";
import { persistMessage } from "../lib/messages";
import { resolvedOutcome } from "../lib/forecast/resolvedOutcome.js";
import { isConcluded } from "../lib/tracking.js";
import { recordUiEvent } from "../lib/logRingBuffer";
import { recordContractKey } from "../lib/contractTelemetry";
import { execTable } from "../store/execTable.js";
import { bindIntent } from "../lib/telemetry/intentDeps.js";
import { beginIntent, type BeginIntentOptions } from "../lib/telemetry/instrument.js";
import type { CommandDeps } from "./deps.js";
import { toast } from "../lib/ui/toast.js";

type AnyFn = (...args: any[]) => any;

/** Deps for the reforecast command: the shared base + the React handles it needs. */
export interface ReforecastDeps extends CommandDeps {
  ui: {
    forecastTracking: boolean;
    forecastLiveProgressEnabled: boolean;
    active: any;
    loading: boolean;
    forecastEngine: string;
    mockToday: string | null;
    setLoading: AnyFn;
    setForecastingPhase: AnyFn;
    setLastObs: AnyFn;
  };
  callbacks: { startResolverPoll: AnyFn };
  refs?: {
    slideCache?: { current: Record<string, unknown> };
    forecastLiveProgressEnabledRef?: { current: boolean };
  };
  tour: { getTourFixture: AnyFn; getTourBranchIncrease: AnyFn };
}

/** Whether the typed live-progress lane is on for this run (the ref wins over the render flag). */
function liveProgressOn(deps: ReforecastDeps): boolean {
  return (
    deps.refs?.forecastLiveProgressEnabledRef?.current ??
    deps.ui.forecastLiveProgressEnabled === true
  );
}

/** Drop every cached slide anchor in one row's `<owner>::` namespace. */
function clearRowAnchors(deps: ReforecastDeps, ownerId: string): void {
  const cache = deps.refs?.slideCache?.current;
  if (!cache) return;
  const prefix = `${ownerId}::`;
  for (const key of Object.keys(cache)) if (key.startsWith(prefix)) delete cache[key];
}

/** Lift one row's `<owner>::` anchors out of the cache, returning what was held. */
function takeRowAnchors(deps: ReforecastDeps, ownerId: string): Record<string, unknown> {
  const cache = deps.refs?.slideCache?.current;
  const taken: Record<string, unknown> = {};
  if (!cache) return taken;
  const prefix = `${ownerId}::`;
  for (const key of Object.keys(cache)) {
    if (!key.startsWith(prefix)) continue;
    taken[key] = cache[key];
    delete cache[key];
  }
  return taken;
}

/** The only typed frames that can put a live node on the timeline. */
const TRIGGER_FRAME_KINDS = new Set(["past_trigger_upserted", "future_trigger_upserted"]);

const bearsTrigger = (frame: unknown): boolean =>
  frame !== null &&
  typeof frame === "object" &&
  TRIGGER_FRAME_KINDS.has(String((frame as Record<string, unknown>).kind));

/** A row's typed-frame sink; `restore()` undoes its anchor clear when the rerun fails. */
interface LiveFrameSink {
  (frame: unknown): void;
  restore(): void;
}

/**
 * The typed-frame sink for one row's rerun. Every frame folds into the run's progress doc as
 * before; on the FIRST TRIGGER-BEARING frame it lifts the row's cached anchors out of the cache
 * (keeping them aside), and `restore()` puts them back if the rerun then fails.
 *
 * Why clear at all: anchors are keyed `<owner>::<event id>` with no run scoping, and the
 * server reuses positional ids (e1..eN) for different events on every run. An entry left over
 * from the previous vintage would place the incoming live node at the previous event's
 * position — `eventAnchorMs` consults the cache before the event's own date — and the
 * done-time reconcile, seeing the same id with the same geometry, would then keep that
 * misplacement as a "held" anchor.
 *
 * Why on the first trigger frame and not before the request or on `stream_opened`: only a
 * `past_trigger_upserted` / `future_trigger_upserted` can put a live node on the timeline (the
 * live projection reads the doc's trigger lists), so that is the latest safe moment, and a
 * rerun that fails before it — rejected outright, or after only its opening frames — has
 * replaced nothing yet.
 *
 * Why keep the lifted anchors and restore on failure: both failure paths retain the OLD
 * timeline (the main path's `runErrored` changes only status; the branch catch re-completes
 * with `br.events`), so a row whose rerun fails after triggers streamed must get its
 * collision-slide anchors back, or it re-renders in new positions though it was never
 * replaced. `restore()` also drops whatever the failed rerun's live nodes cached meanwhile.
 * On success the done-time reconcile owns the namespace. The accumulated past re-derives its
 * anchors from unchanged inputs on the next layout pass. This sink only exists when the live
 * lane is on; with it off there are no live nodes to misplace and the done-time path clears
 * the row.
 */
function liveFrameSink(deps: ReforecastDeps, runId: string, ownerId: string): LiveFrameSink {
  const dispatchRun = deps.store.dispatch;
  let lifted: Record<string, unknown> | null = null;
  const sink = ((frame: unknown) => {
    if (lifted == null && bearsTrigger(frame)) lifted = takeRowAnchors(deps, ownerId);
    applyUpdateFrame(dispatchRun, deps.store.getState, runId, frame);
  }) as LiveFrameSink;
  sink.restore = () => {
    if (lifted == null) return;
    const cache = deps.refs?.slideCache?.current;
    if (cache) {
      clearRowAnchors(deps, ownerId);
      Object.assign(cache, lifted);
    }
    lifted = null;
  };
  return sink;
}

/** Preserve exact live anchors or invalidate only the completing row's cache namespace. */
function reconcileCompletionAnchors(
  deps: ReforecastDeps,
  runId: string,
  ownerId: string,
  finalEvents: any[],
): boolean {
  const progressDoc = selectRun(deps.store.getState(), runId)?.progressDoc;
  const liveEvents = progressDoc ? projectLiveEvents(progressDoc) : [];
  const reconciled =
    liveProgressOn(deps) &&
    // Reforecast shape: `finalEvents` is the ACCUMULATED timeline — prior-vintage past the
    // live projection never carried, plus the fresh vintage under its own ids — so the
    // fresh-launch equal-set check does not hold in general; hold the surviving live ids.
    liveEventsReconcileInPlace(liveEvents, finalEvents, { accumulated: true });
  if (!reconciled) clearRowAnchors(deps, ownerId);
  return reconciled;
}

/**
 * Save a rerun that landed on a forecast shared with the viewer. The persister saves only the
 * viewer's own forecasts, so without this the superseded vintage's history entry and the
 * merged timeline would stay in this tab. The body carries the owner's `forecastId`, so the
 * server writes the owner's row after re-checking that the viewer may edit it.
 */
/**
 * Whether any run of the forecast rooted at `rootId` lacks its detail: the root and every branch
 * under it by parent, the same tree the rerun fans out over.
 */
function awaitingDetailIn(state: any, rootId: string | null | undefined): boolean {
  return !!rootId && selectRunTree(state, rootId).some(lacksDetail);
}

function saveSharedRerun(deps: CommandDeps, runId: string, liveProgressEnabled: boolean): void {
  const state = deps.store.getState();
  const run = state.sharedRunsById?.[runId];
  if (!run) return;
  const f: any = runToLegacyForecast(run, state, liveProgressEnabled);
  if (!f || f._pending || f.detailPending) return;
  persistForecast(deps, f, JSON.stringify(stripDebugForWire(f)));
}

/** How long Update now waits on its scenario-trigger checks, and how often it polls them: the
 *  main question's resolve gate, which they run beside, keeps the same clock. */
const PREMISE_CHECK_MAX_MS = 5 * 60 * 1000;
const PREMISE_CHECK_POLL_MS = 10 * 1000;

/** Poll answers that mean "ask again": the request never arrived, or the server could not reach
 *  the resolver this time. */
const PREMISE_POLL_RETRY_STATUSES = new Set([0, 502]);

/**
 * Ask the resolver now whether each pending scenario trigger under `rootRunId` has happened: every
 * branch whose premise is unsettled, gradable, keyed to a trigger contract and inside its window.
 * The server records a settled answer and grades the branch before it replies; the branch's new
 * premise record goes into the store here, so the timeline shows it and the rerun's dead-branch
 * check reads it. Resolves to the number of checks made once each has answered or timed out.
 */
async function checkPendingPremises(
  deps: CommandDeps,
  rootRunId: string,
  nowMs: number,
): Promise<number> {
  const api = deps.services.api;
  if (!api?.submitPremiseCheck || !api?.pollPremiseCheck) return 0;
  const state = deps.store.getState();
  const branches = selectUnarchivedBranchRuns(state, rootRunId);
  const views = premiseViewsFor({
    branchRuns: branches,
    runsById: selectPremiseRunsById(state),
    nowMs,
  });
  const due = branches.filter((br: any) => {
    const view = br.runId ? views[br.runId] : null;
    return (
      !!br.forecastId &&
      !!view &&
      !view.settled &&
      view.gradable &&
      !!view.resolvableKey &&
      premiseAttemptWarranted({
        reason: "reforecast",
        state: view.state,
        window: view.window,
        nowMs,
      })
    );
  });
  const check = async (br: any): Promise<void> => {
    const deadline = new AbortController();
    const timer = setTimeout(() => deadline.abort(), PREMISE_CHECK_MAX_MS);
    const options = { signal: deadline.signal };
    try {
      const submitted = await api.submitPremiseCheck(br.runId, br.forecastId, options);
      if (!submitted.traceId) {
        console.warn("[reforecast] premise check not started", br.runId, submitted.status);
        return;
      }
      while (!deadline.signal.aborted) {
        await new Promise((r) => setTimeout(r, PREMISE_CHECK_POLL_MS));
        if (deadline.signal.aborted) break;
        const polled = await api.pollPremiseCheck(
          br.runId,
          submitted.traceId,
          br.forecastId,
          options,
        );
        if (polled.verdict === "in_flight" || PREMISE_POLL_RETRY_STATUSES.has(polled.status))
          continue;
        if (!polled.ok) {
          console.warn("[reforecast] premise check failed", br.runId, polled.status);
          return;
        }
        const run = selectRun(deps.store.getState(), br.runId);
        const premise = run ? nextPremiseRecord(run.premise, polled.premise) : null;
        if (premise) deps.store.dispatch(premiseCleared(br.runId, premise));
        return;
      }
      console.warn("[reforecast] premise check timed out", br.runId);
    } finally {
      clearTimeout(timer);
    }
  };
  await Promise.all(due.map(check));
  return due.length;
}

/** An event's date as a sort key, missing or invalid first (the merge's own order). */
const eventDateMs = (e: any): number => {
  const ms = e?.date ? new Date(e.date).getTime() : NaN;
  return Number.isNaN(ms) ? -Infinity : ms;
};

/**
 * A rerun's merged `events` for `parentRunId` with each graded child scenario's trigger kept and
 * marked as it turned out, the mark the server put on the parent's row when it graded the
 * branch (branchPremiseSync). The merge rebuilds the events from the old and fresh runs, and the
 * root's are then saved whole, so without this a mark this tab has not loaded would be lost, and
 * so would a trigger that happened before its window closed if the fresh run left it out (it
 * sits in the refreshed future half). The trigger is found by its terms
 * (`_sourceEventIdentity`), since a fresh run may renumber its events. `branches` includes
 * cleared scenarios: clearing one does not change what happened.
 */
function withPremiseOutcomes(
  events: any[],
  oldEvents: any[],
  branches: any[],
  parentRunId: string,
): any[] {
  let out = events;
  let added = false;
  for (const br of branches) {
    const premise = br?.premise;
    const frozen = br?.forkEvent?._sourceEventIdentity;
    if (br?.parentRunId !== parentRunId || !isTerminalPremiseState(premise?.state)) continue;
    if (typeof premise.verdict !== "string" || !premise.verdict || !frozen) continue;
    const occurred = premise.verdict === "resolved_yes";
    const isSource = (e: any) => sameSourceTerms(e, br.forkEvent);
    if (!out.some(isSource)) {
      const kept = oldEvents.find(isSource);
      if (kept) {
        // A fresh event for the same occurrence (reworded, same title and date) gives way to it.
        const key = kept.occurrenceKey ?? occurrenceKeyOf(kept);
        const at = key
          ? out.findIndex((e) => (e?.occurrenceKey ?? occurrenceKeyOf(e)) === key)
          : -1;
        out = at >= 0 ? out.map((e, i) => (i === at ? kept : e)) : [...out, kept];
        added = true;
      }
    }
    if (out.some((e) => e?.occurred !== occurred && isSource(e)))
      out = out.map((e) => (e?.occurred !== occurred && isSource(e) ? { ...e, occurred } : e));
  }
  return added ? [...out].sort((a, b) => eventDateMs(a) - eventDateMs(b)) : out;
}

/**
 * Mark graded scenario outcomes on the stored events of the root and of each parent scenario
 * under it, so any save of those runs (a failed or skipped rerun's included) keeps the marks.
 */
function markPremiseOutcomes(deps: CommandDeps, rootRunId: string): void {
  const state = deps.store.getState();
  const branches = selectBranchRuns(state, rootRunId);
  for (const run of [selectRun(state, rootRunId), ...branches]) {
    const events = Array.isArray(run?.events) ? run.events : null;
    if (!run?.runId || !events) continue;
    const marked = withPremiseOutcomes(events, events, branches, run.runId);
    if (marked !== events) deps.store.dispatch(premiseOutcomesMarked(run.runId, marked));
  }
}

/**
 * Resubmit the active forecast (and every branch) through the same orchestrator, merging
 * fresh results onto the old. No-op when tracking is off, there is no active forecast, or
 * it's concluded. May run CONCURRENTLY with another in-flight run — reforecasting a
 * DIFFERENT forecast no longer blocks on the global `loading` (mirrors the `force` bypass
 * in submitForecast, so it behaves like starting multiple fresh forecasts at once).
 * Per-forecast double-run is prevented by the execTable.claim(fcId) below, not by `loading`.
 * Non-abortable by design (§7).
 */
export async function reforecast(
  rawDeps: ReforecastDeps,
  {
    origin,
    surface,
  }: { origin?: BeginIntentOptions["origin"]; surface?: BeginIntentOptions["surface"] } = {},
): Promise<void> {
  const {
    forecastTracking,
    forecastLiveProgressEnabled,
    active,
    loading,
    forecastEngine,
    mockToday,
    setLoading,
    setForecastingPhase,
    setLastObs,
  } = rawDeps.ui;
  const { startResolverPoll } = rawDeps.callbacks;
  const { getTourFixture, getTourBranchIncrease } = rawDeps.tour;
  if (!forecastTracking || !active) return;
  // The Update now button's own predicate, checked again here for any other caller: the owner
  // or an in-org collaborator (the server's `canEdit`), never another organization's viewer.
  // A row with no `canEdit` reads as editable (every owner-leg row lacks it); the stream proxy
  // enforces the rule regardless (`editBranches`), so a wrong answer costs a refused request,
  // never an unauthorized run.
  if (!canUpdateNow(active)) return;
  // Complete-on-creation machine branches are immutable projections of the source
  // trigger's conditional. Guard the active row before resolver, claim, or model work;
  // the branch fan-out has the same exclusion below, but it cannot protect an active
  // derived row that is invoked directly after hydration.
  if (active.forkDerived) return;
  // The rerun supersedes the forecast's current vintage and merges its events, root and branches
  // alike; on a lean row both are empty, and the save would overwrite the real ones. Update now
  // waits for the detail read (updateNowState), and so does any other caller.
  if (lacksDetail(active) || awaitingDetailIn(rawDeps.store.getState(), active.id)) return;
  const q = active.question || active.contract?.question || active.contract?.title;
  if (!q) return;
  // Honor the debug "Set today's date & time" override — when the user has
  // pushed the mock today forward, the event partition (and thus the
  // Resolver placeholder fan-out) treats THAT instant as "now" so testing
  // the reforecast flow doesn't require waiting actual wall-clock time.
  const nowDate = mockToday ? new Date(mockToday) : new Date();
  // Only a CONCLUDED forecast — a terminal verdict, a named multiway slot, or a
  // void (lib/tracking.js isConcluded) — is over; manual reforecasting is no longer
  // offered. An unresolved forecast (incl. past its date with no verdict) can still
  // be reforecast; the ↻ button stays available (App.jsx). This guards any other
  // caller so the flow can't be driven on a terminally-settled forecast.
  if (isConcluded(active)) return;
  // The user's reforecast (P2 design §4.6): it follows the main run, wherever the user is
  // meanwhile (the branch reruns it fans out carry the intent but do not end it).
  const intent = beginIntent(
    "forecast.reforecast",
    { forecast_id: active.id ?? null, parent_id: active.id ?? null },
    { origin: origin ?? "timeline.reforecast", ...(surface ? { surface } : {}) },
  );
  if (active.id) intent.attachRun(active.id);
  const deps = bindIntent(rawDeps, intent);
  const { store, services } = deps;
  const dispatchRun = store.dispatch;
  const { genForecastFromServer, streamBranch } = services.forecastClient;
  // Snapshotted into oldSnapshot.events below (the vintage being superseded).
  // Also the source of the ACCUMULATED past: mergeReforecastEvents keeps every
  // past event from this prior vintage and only refreshes the future half from
  // the fresh server response — so past triggers build up over time. See
  // reforecastEvents.js.
  const oldEvents = active.events || [];
  // The question's multi-value outcome space, resolved ONCE for this whole reforecast — the
  // main spine's rerun, every branch's rerun, and the contract we land afterwards. It has to
  // be re-sent on every request because it lives on the contract, not in the question text:
  // omit it and the forecast server re-authors the question as BINARY, so a multi-value
  // forecast changes shape on a rerun. `outcomeSpaceOfRun` falls back to the run's history
  // vintages, so a root already corrupted by one earlier binary rerun heals here instead of
  // staying binary forever.
  const outcomeSpace = outcomeSpaceOfRun(active);

  // Snapshot the OLD state before we replace any of it. Pushed to
  // active.history below so the report modal can navigate back to it.
  //
  // `snapshotAt` is the moment this reforecast is happening — i.e., the
  // END of this snapshot's probability "lifespan." Used by the
  // probability-over-time plot line to know when each historical
  // segment ended. `generatedAt` stays at active.createdAt for backward
  // compatibility with the history-nav arrows in the report modal,
  // which read that field as the displayed "Report generated on" date.
  //
  // Anchor the moment to `nowDate` (mock-today aware) NOT real
  // wall-clock — otherwise pushing the today line forward via the
  // debug picker and then reforecasting stamps the snapshot at real
  // now, which sits AT the GENERATED line on the timeline. The past
  // segment then has near-zero visible width, the new "current"
  // segment covers the entire mocked past at the new probability,
  // and no transition or dot is visible at where the user perceives
  // the reforecast moment to be. Using `nowDate` puts the snapshot
  // boundary where the TODAY line was when the user clicked
  // REFORECAST, which is what they expect.
  let reforecastMomentIso = nowDate.toISOString();
  const oldSnapshot = {
    // When the snapshotted forecast was last (re)generated — lastReforecastedAt
    // for an already-reforecasted forecast, else the original createdAt. Using
    // createdAt unconditionally made every history entry share one timestamp,
    // so report-history nav couldn't show a meaningful per-snapshot "generated on".
    generatedAt: active.lastReforecastedAt || active.createdAt || null,
    snapshotAt: reforecastMomentIso,
    contract: active.contract || null,
    events: oldEvents,
    report: active.report || null,
    serverContext: active.serverContext || null,
    serverExplanation: active.serverExplanation || null,
    // The headline, which can differ from contract.probability: a rollback restores it.
    serverPrediction: active.serverPrediction ?? null,
  };

  const fcId = active.id;

  // Claim exec ownership of the main run BEFORE touching loading/run state, and
  // BAIL if it fails — the run is already streaming in this tab (a resume-sweep
  // reattach, or another reforecast), so proceeding would open a SECOND stream on
  // the same runId, the exact race this guards against. Claiming first (not after
  // setLoading/runStarted) means a failed claim leaves no stuck loading spinner or
  // orphaned RUNNING run. Mirrors the branch path's `if(!claim)return br`.
  // Non-abortable (§7): no abortController. Released in the finally below.
  let claimedMain = false;
  if (fcId) {
    if (!execTable.claim(fcId, { kind: "main", clientId: fcId, abortController: null })) {
      intent.end("error");
      return;
    }
    claimedMain = true;
  }

  // The global `loading`/`forecastingPhase` track the FOREGROUND run only (the input
  // spinner + Cancel affordance). A reforecast started while another run already owns
  // the foreground runs as a BACKGROUND run: it drives its own progress purely through
  // the run-store (runStarted/runProgress below) and must NOT touch the shared
  // loading/phase, or finishing first would clear the foreground run's spinner. Capture
  // ownership now (before we flip loading) — `loading` is false ⇒ nothing else owns the
  // foreground ⇒ this reforecast does. Mirrors submitForecast's forecastAbortRef guard.
  const ownsForeground = !loading;
  if (ownsForeground) {
    setLoading(true);
    setForecastingPhase("awaiting");
  }

  // Acknowledge the click BEFORE the slow resolve below. Every user-visible sign that a
  // reforecast started is downstream of RUN_STARTED: it stamps `lastReforecastedAt` (whose
  // cooldown retires the ↻ pill) and moves the run to RUNNING (which is what gives the chat
  // console a live line). Ordering it after the gate is what made a press look ignored.
  // The settled path below unwinds this, since no reforecast then happened.
  const priorReforecastedAt = active.lastReforecastedAt ?? null;
  if (fcId) {
    dispatchRun(runStarted(fcId, reforecastMomentIso));
    // Name the phase we are actually in. `stepIdx: -1` marks it off-ladder, so the
    // reducer seeds it as a message-only line and the first REAL frame (stepIdx >= 0)
    // supersedes it under the monotonic guard rather than being rejected by it.
    dispatchRun(
      runProgress(fcId, {
        stage: null,
        step: null,
        stepIdx: -1,
        pct: null,
        message: "Checking for a resolution…",
      }),
    );
  }

  // Each scenario trigger still pending is checked beside the question's own resolve, so a
  // trigger that happened since the last look is graded before the reruns below need it.
  let premisesChecked = false;
  const premiseChecks = fcId
    ? checkPendingPremises(deps, fcId, nowDate.getTime())
        .catch((e) => {
          console.warn("[reforecast] premise checks threw; rerunning anyway:", e);
          return 0;
        })
        .finally(() => {
          premisesChecked = true;
        })
    : Promise.resolve(0);

  // GATE → RESOLVE → FORECAST. Attempt a resolve FIRST and forecast only if the question is
  // still open — a settled question gets no fresh forecast. This replaces the trailing
  // resolve that used to fire after the forecast, with parity: the poll stamps the verdict
  // into the run and the server persists it on the caller's row, exactly as before, and
  // nothing else fans out. FAIL-OPEN: anything short of a terminal verdict — `null` (tracking
  // off, resolver unavailable, timeout, abort), "unresolved", or a thrown error — falls
  // through to the forecast; the poll records the raw reason internally. This now sits
  // AFTER runStarted (see the acknowledge-the-click note above) — the run is already
  // RUNNING and narrating "Checking for a resolution…", and the settled branch below
  // unwinds it. The exec claim above spans this wait, so a second ↻ bails meanwhile.
  let verdict: any = null;
  try {
    verdict = await startResolverPoll(active);
  } catch (e) {
    console.warn("[reforecast] resolve step threw; forecasting anyway:", e);
    recordUiEvent({
      event: "ui.resolver.failed",
      forecastId: active.forecastId || null,
      reason: "threw",
      error: String(e),
    });
  }
  // Before the settled decision below, which must see a verdict that lands meanwhile, and the
  // dead-branch check and merges, which read the premises these checks grade.
  if (!premisesChecked && fcId) {
    dispatchRun(
      runProgress(fcId, {
        stage: null,
        step: null,
        stepIdx: -1,
        pct: null,
        message: "Checking whether scenario triggers happened…",
      }),
    );
  }
  const checksMade = await premiseChecks;
  if (fcId) markPremiseOutcomes(deps, fcId);
  // A premise graded during that wait is dated after the click, and this rerun includes it, so
  // the rerun is dated from here (`premiseIncorporated`). The debug today override keeps its clock.
  if (checksMade > 0 && !mockToday) {
    reforecastMomentIso = new Date().toISOString();
    oldSnapshot.snapshotAt = reforecastMomentIso;
  }
  // Decide on the LIVE run, not the click-time snapshot: while this awaited, the realtime
  // contract.resolved pickup may have stamped a terminal verdict into the store. A terminal
  // state already there settles it; otherwise the verdict the poll returned is overlaid for
  // the decision.
  const liveRun: any = fcId ? selectRun(store.getState(), fcId) : null;
  const gateView = liveRun
    ? {
        ...active,
        contractStatus: liveRun.contractStatus ?? active.contractStatus,
        resolution: liveRun.resolution ?? active.resolution,
      }
    : active;
  const settled =
    isConcluded(gateView) || (!!verdict && isConcluded({ ...gateView, resolution: verdict }));
  if (settled) {
    // No reforecast ran: the unwind below must not read as the rerun completing.
    intent.end("error");
    recordUiEvent({
      event: "ui.forecast.reforecast_skipped_settled",
      forecastId: active.forecastId || null,
      status: verdict?.status ?? gateView.resolution?.status ?? gateView.contractStatus ?? null,
    });
    // Unwind the optimistic RUNNING entered at click: back to COMPLETED on the run's LIVE
    // values, with the cooldown stamp RESTORED — no reforecast happened, so the ↻ pill must
    // not be suppressed for 30 minutes on a question whose verdict may itself be wrong.
    // When the verdict is right `isConcluded` retires the pill anyway, so the stamp is moot.
    // `serverPrediction` must be passed through: RUN_COMPLETED normalizes an omitted one to
    // null, which would clear a persisted headline this path never replaced.
    if (fcId) {
      const cur = liveRun ?? active;
      dispatchRun(
        runCompleted(fcId, {
          contract: cur.contract ?? active.contract,
          events: cur.events ?? oldEvents,
          serverContext: cur.serverContext ?? active.serverContext,
          serverExplanation: cur.serverExplanation ?? active.serverExplanation,
          report: cur.report ?? active.report,
          serverPrediction: cur.serverPrediction ?? null,
          lastReforecastedAt: priorReforecastedAt,
        }),
      );
    }
    // Name the reason in the conversation: a silent return here is indistinguishable from
    // a button that does nothing.
    const settledView = { ...gateView, resolution: verdict ?? gateView.resolution };
    const outcome = resolvedOutcome(settledView, nowDate.getTime());
    const entry = makeEntry({
      role: "assistant",
      runId: fcId ?? null,
      content: outcome?.namesOutcome
        ? `This question has already resolved: ${
            outcome.label === "OCCURRED"
              ? "it happened"
              : outcome.label === "DID NOT OCCUR"
                ? "it didn't happen"
                : outcome.label
          }. Sayer didn't update it.`
        : "This question is already settled, so Sayer didn't update it.",
      ts: reforecastMomentIso,
    });
    if (fcId) {
      dispatchRun(entryAppended(summaryConvId(store.getState(), fcId), entry));
      // Best-effort persistence, same as every other command-posted line: without it the
      // explanation evaporates on reload and the next visit is back to the silent button.
      const persistId = selectRun(store.getState(), fcId)?.forecastId || active.forecastId;
      if (persistId) void persistMessage(persistId, entryToMessageBody(entry));
    }
    // Mirrors the `finally` below, which this early return does not reach: no intro timer
    // exists yet, so only the foreground state and the claim need unwinding.
    if (ownsForeground) {
      setLoading(false);
      setForecastingPhase(null);
    }
    if (claimedMain) execTable.release(fcId);
    return;
  }
  // RUNNING + the progress reset already happened at click time (above), so the ladder
  // climbs from the start without a second RUN_STARTED here.
  // Paced opening-steps intro (mirrors submitForecast): the backend blows through the
  // early ladder steps in <1s, so on a reforecast the line would otherwise snap to the
  // first real phase. Pace those opening steps on a short timer so the climb reads as a
  // progression from the start. Real onProgress frames are DEFERRED (below) until it
  // hands off. Always stop()ed in the finally to clear timers.
  const intro = fcId ? startProgressIntro(dispatchRun, fcId, forecastEngine) : null;
  const onReforecastProgress = (frame: any) => {
    const { stage, step, progress, data } = frame;
    const payload = progressPayload(stage, progress, step, data, forecastEngine);
    // Defer real frames while the paced intro owns the line: the latest one is applied
    // at hand-off, so it neither jumps ahead of the opening steps nor is dropped. Only a
    // recognized phase may take the intro's single slot, or it would displace a usable one.
    if (intro?.active() && payload.stepIdx < 0) return;
    if (intro?.defer(() => onReforecastProgress(frame))) return;
    if (fcId) dispatchRun(runProgress(fcId, payload));
  };
  // The root row's typed-frame sink (see liveFrameSink), created just before its stream call
  // and consulted by the catch below so a failed rerun gives the row its anchors back.
  let mainSink: LiveFrameSink | null = null;
  try {
    // ─── Reforecast every branch in parallel ────────────────────────────
    // Each branch gets its own server call so the dashed-line probability
    // and trigger events catch up too. The fork event (the ✓/✕ pie that
    // anchors the branch) is always preserved as-is — past triggers were
    // already historical, and future triggers were user-selected. Other
    // branch events follow the same end-date partition as the main
    // forecast: ends-before-today preserved, ends-at-or-after-today
    // replaced by the new server response.
    //
    // To give per-branch visual feedback (matching the placeholder-
    // creation pattern at line ~2225 — same `_loading:true` flag the
    // branch row already renders as a pulsing "Generating Forecast"
    // label), branches are marked loading IMMEDIATELY after the main
    // reforecast lands, then the flag is cleared per-branch as each
    // server call returns. setActive uses the prev.id===fcId guard so
    // a mid-flight navigation to another forecast doesn't stomp the
    // wrong row.
    //
    // All the branch resolution below (branchAssumption / reforecastBranches
    // / reforecastParentFc / parentFcFor), the RUNNING placeholder fan-out,
    // and the branchPromises fan-out read only from the live store + args —
    // NEVER from the main result `fc` — so they run BEFORE the multi-minute
    // main `await`: branch placeholders pulse immediately and every branch
    // rerun streams in parallel with the spine instead of after it.
    const branchAssumption = (br: any) => {
      // Complete-on-creation branches are frozen projections of the source
      // trigger's a/b conditionals. Reforecasting one would buy a new model run
      // and silently replace that immutable derivation.
      if (br.forkDerived) return null;
      const fe = br.forkEvent;
      if (!fe) return null;
      // occur keeps the trigger's direction; not-occur inverts it (see branchDirContext).
      const occurCtx = branchDirContext(fe.direction);
      const absentCtx = branchDirContext(fe.direction, { absent: true });
      // Custom-trigger branches are created via handleCustomEventBranch
      // and stamp `ce-` ids on the placeholder fork; their canonical
      // assumption mirrors the one used at creation time.
      if (typeof fe.id === "string" && fe.id.startsWith("ce-")) {
        return customEventAssumption({ date: fe.date, title: fe.title });
      }
      // Occur / doesn't-occur branches — match the assumption strings in
      // genBranch() at the top of the file so the server gets the same
      // counterfactual phrasing it saw originally.
      const p = fe.p_event != null ? fe.p_event : fe.probability;
      if (p === 1 || p >= 0.9)
        return occurAssumption({ title: fe.title, date: fe.date, dirContext: occurCtx });
      if (p === 0 || p <= 0.1)
        return notOccurAssumption({ title: fe.title, date: fe.date, dirContext: absentCtx });
      return null;
    };
    // The active forecast's branch RUN objects, read once
    // from the live store (selectRunTree(...).slice(1)) and reused across the
    // reforecast block. Same order/identity as the legacy active.branches[] was.
    // Field map applied at each use: id → runId, parentBranchId → parentRunId
    // (a direct child of the root has parentRunId === conversationId === root runId),
    // probability → contract?.probability; forkEvent/events/contract/serverContext/
    // serverExplanation/report map 1:1.
    // ARCHIVED BRANCHES (CLEAR BRANCH) are not re-forecast either — nor anything under one.
    // They are off the live lane and untracked; the stream proxy refuses them (409) too.
    const reforecastBranches = selectUnarchivedBranchRuns(store.getState(), active?.id);
    // DEAD BRANCHES ARE NOT RE-FORECAST. A branch whose premise was contradicted is
    // counterfactual: the world went the other way, so re-running it would spend a
    // forecast on a path that cannot happen and would refresh a probability the UI
    // has already replaced with a premise verdict. Skipped BEFORE the RUNNING
    // placeholder fan-out below, so a dead branch never even pulses.
    //
    // Uses `nowDate` (the mock-today-aware clock this whole command runs on), not
    // wall-clock, so pushing the debug today line past a trigger's window kills the
    // branch here exactly when the timeline says it died.
    //
    // A CONFIRMED premise is deliberately NOT skipped: the condition held, so the
    // branch is now a live conditional forecast of the real world and must keep
    // re-forecasting — that is the whole point of confirming it.
    const branchPremises = premiseViewsFor({
      branchRuns: reforecastBranches,
      runsById: selectPremiseRunsById(store.getState()),
      nowMs: nowDate.getTime(),
    });
    const isDeadBranch = (br: any) =>
      !!br?.runId && branchPremises[br.runId]?.state === "contradicted";
    // Each branch rerun is a counterfactual whose run-start needs the root's
    // parent_forecast_id (proxy 400s without it). Resolve it once from the live
    // store; if the root has no server id (errored / id-less), skip the branch
    // reruns entirely rather than fire N doomed 400s + leave them stuck pulsing.
    const reforecastParentFc =
      (active.id && selectRun(store.getState(), active.id)?.forecastId) ||
      active.forecastId ||
      null;
    // Each rerun's parent_forecast_id must be its IMMEDIATE parent's server id —
    // the root for a direct branch, or the PARENT BRANCH for a sub-branch.
    // reforecastBranches is the flat branch RUN tree (includes sub-branches whose
    // parentRunId points at another branch), so resolve per-branch: using the
    // root's id for a sub-branch would mis-attribute the rerun's lifecycle FK.
    // A DIRECT child of the root has parentRunId ===
    // active.id (= the root's runId/conversationId) — the run-store equivalent of
    // the legacy null parentBranchId — so it falls back to reforecastParentFc; only
    // a true sub-branch (parentRunId points at another branch) looks up a parent FK.
    const parentFcFor = (br: any) =>
      br.parentRunId && br.parentRunId !== active.id
        ? selectRun(store.getState(), br.parentRunId)?.forecastId ||
          reforecastBranches.find((b: any) => b.runId === br.parentRunId)?.forecastId ||
          null
        : reforecastParentFc;
    // Mark each refreshable branch's run RUNNING so the _loading pulse
    // actually shows (the branches[] _loading:true on mainStateReforecasted
    // is a no-op — the compat selector derives _loading from the branch
    // run's STATUS in runsById, not from the main run's .branches property).
    // This fan-out runs BEFORE the branchPromises map below so every branch
    // is RUNNING before its own rerun promise begins — and before the main
    // `await` below so the pulses light up immediately, not after the spine.
    // On a shared forecast, record the update before any branch rerun starts, on the root's
    // current trace (re-stamped when the main run allocates its own): a reload then finds the
    // branch reruns even when the main request fails before allocating a trace.
    const sharedRootTrace = fcId ? store.getState().sharedRunsById?.[fcId]?.traceId : null;
    if (fcId && sharedRootTrace) writeSharedRerunHandle(fcId, sharedRootTrace);
    for (const br of reforecastBranches) {
      if (isDeadBranch(br)) continue;
      if (branchAssumption(br) && parentFcFor(br)) {
        dispatchRun(runStarted(br.runId, reforecastMomentIso));
      }
    }
    const branchPromises = reforecastBranches.map(async (br: any) => {
      // Dead branch (premise contradicted) — return it untouched. No claim, no
      // stream, no store dispatch: its rendered state is already terminal.
      if (isDeadBranch(br)) return br;
      const assumption = branchAssumption(br);
      if (!assumption) return br; // unsupported modType: skip cleanly, no _loading flicker
      const brParentFc = parentFcFor(br);
      if (!brParentFc) return br; // parent has no server id — skip (would 400)
      // Claim exec ownership of THIS branch run for its rerun (mirrors createBranch)
      // so the resume-sweep's `has(runId)` skip fires and reattachBranch can't race
      // the live rerun on reload/refocus. Released on settle below.
      if (
        !execTable.claim(br.runId, {
          kind: "branch",
          clientId: br.runId,
          abortController: null,
        })
      )
        return br; // already streaming in this tab
      // The branch row's typed-frame sink (see liveFrameSink); the catch below restores the
      // anchors it lifted when the rerun fails.
      const brSink = forecastLiveProgressEnabled
        ? liveFrameSink(deps, br.runId, `branch-${br.runId}`)
        : null;
      // _loading:true pulse driven by runStarted(br.id) dispatched above.
      try {
        // Route this RERUN through the SINGLE branch SSE
        // path — streamBranch — instead of a second, hand-rolled
        // assumption-injected genForecastFromServer call. This is option (b) from
        // the plan: reuse the shared stream+stamp TAIL (streamBranch already owns
        // the assumption-injected SSE call + parentForecastId/clientId wiring +
        // trace stamping), NOT createBranch itself. We deliberately do NOT route
        // through createBranch because a reforecast RE-RUNS an existing branch run
        // (br.id already in the store, runStarted above — not a fresh `br-`+uid
        // placeholder), and it carries reforecast-only lifecycle that the create
        // path must never grow: the fork-event-preserving event MERGE below,
        // runCompleted-on-error (re-complete with OLD values to clear the pulse,
        // not runErrored), no startBranchAnim, and no per-branch chat line. Folding
        // those into createBranch via an existingRunId flag (option a) would bloat
        // and risk regressing the frozen create path; keeping them here while the
        // SSE call goes through streamBranch satisfies the invariant — exactly ONE
        // branch SSE path — without touching createBranch at all.
        //
        // Parent identity comes FROM THE STORE (same one-place rule): brParentFc
        // was resolved above by the parent RUN's runId via parentFcFor(br)
        // (br.parentBranchId's run for a sub-branch, the root otherwise). The
        // reforecast trigger is built pass-through (branchTrigger reforecast kind):
        // assumption from branchAssumption(br), the rest carried off the branch.
        const brFc = await streamBranch(q, forecastEngine, assumption, null, {
          parentForecastId: brParentFc,
          // Re-run under the branch's OWN runId as client_id — the SAME id
          // createBranch persisted it under (createBranch streams with
          // clientId:branchRunId, and runId===branchRunId). The server upserts the
          // lifecycle row by (user_id, client_id) and round-trips client_id as the
          // public `id`, so this UPDATES the branch's existing row instead of
          // inserting a second one. The previous synthesized
          // `${active.id}-branch-${br.runId}` never matched the branch's own row, so
          // every reforecast leaked a duplicate, fork-less branch row that hydrated
          // as a stray "Branch — Prediction: N%" peer on reload (bug cad354d1).
          // br.runId is stable across reforecasts, so this still never leaks a new
          // row per run.
          clientId: br.runId,
          forecastId: selectRun(store.getState(), br.runId)?.forecastId || br.forecastId,
          // Carry the branch's fork metadata (as createBranch does) so the reused
          // row keeps its fork column — the rerun then renders as the branch's own
          // "Branch: <mod> — Prediction: N%" line, not a fork-less peer.
          forkDate: br.forkDate,
          modification: br.modification,
          forkEvent: br.forkEvent,
          // A branch of a multi-value parent is a distribution too — its own space when it
          // has one, else the root's (resolved above). Without this the fan-out streamed
          // every branch with NO space, so each reforecast of a multiway root re-authored
          // all of its branches as binary: the create path (`branchForecast.ts`) has always
          // sent the parent's space, and this rerun path silently did not.
          outcomeSpace: outcomeSpaceOfRun(br) || outcomeSpace,
          onTraceAllocated: ({ traceId, forecastId, backend }: any) => {
            // Branch rerun re-stamps its existing run's trace so reload reattach
            // recovers the correct vintage per branch (facet c).
            dispatchRun(traceReallocated(br.runId, { traceId, forecastId, backend }));
          },
          tourFixture: getTourFixture(),
          tourBranchIncrease: getTourBranchIncrease(),
          // Folds typed frames into the branch run; lifts the branch row's previous-vintage
          // anchors on the first trigger frame (see liveFrameSink; restored in the catch).
          onUpdate: brSink ?? undefined,
          // Narration bears no trigger, so it needs none of liveFrameSink's
          // anchor-lifting — just the same flag and the same run.
          onNarration: forecastLiveProgressEnabled
            ? (frame: unknown) =>
                applyNarrationFrame(dispatchRun, deps.store.getState, br.runId, frame)
            : undefined,
        });
        const fe = br.forkEvent;
        // mergeReforecastEvents: same accumulate-past / refresh-future merge as
        // the main path — keep the branch's prior past events, add newly-occurred
        // ones, refresh the future from the fresh rerun — while always keeping the
        // fork event. Deduped + sorted by date. The merge preserves each event's
        // run id and stamps the stable `occurrenceKey` the live nodes are keyed
        // on, so the branch's nodes reconcile in place at done.
        const mergedBrEvents = withPremiseOutcomes(
          mergeReforecastEvents({
            oldEvents: br.events || [],
            newEvents: brFc.events || [],
            nowDate,
            preserveEvent: fe,
          }),
          br.events || [],
          selectBranchRuns(store.getState(), active.id),
          br.runId,
        );
        // streamBranch normalizes an absent serverContext to `{}` (vs the raw
        // genForecastFromServer's `undefined`); treat that empty object as absent
        // so the "keep the old branch's context when the rerun omits it" fallback
        // still fires — preserving the prior genForecastFromServer behavior exactly.
        const brServerContext =
          brFc.serverContext && Object.keys(brFc.serverContext).length
            ? brFc.serverContext
            : br.serverContext;
        const refreshed = {
          ...br,
          events: mergedBrEvents,
          probability: brFc.contract?.probability ?? br.contract?.probability ?? null,
          serverPrediction: brFc.serverPrediction ?? null,
          serverExplanation: brFc.serverExplanation || br.serverExplanation,
          serverContext: brServerContext,
          // Same shape-preservation as the spine's `preservedContract` below: keep the
          // space on the landed branch contract so a SUB-branch forked off this branch
          // (which reads its parent's contract) is still told it, even if this rerun
          // itself came back with none.
          contract: withOutcomeSpace(
            brFc.contract || br.contract,
            outcomeSpaceOfRun(br) || outcomeSpace,
          ),
          report: brFc.report || br.report,
          _loading: false,
        };
        reconcileCompletionAnchors(deps, br.runId, `branch-${br.runId}`, refreshed.events);
        // 2. Land the refreshed branch in state via runCompleted — sets
        //    status COMPLETED (_loading:false) and stores new contract/events.
        dispatchRun(
          runCompleted(br.runId, {
            contract: refreshed.contract,
            serverPrediction: refreshed.serverPrediction,
            events: refreshed.events,
            serverContext: refreshed.serverContext,
            serverExplanation: refreshed.serverExplanation,
            report: refreshed.report,
          }),
        );
        saveSharedRerun(deps, br.runId, forecastLiveProgressEnabled);
        execTable.release(br.runId); // release the claim taken above (success)
        return refreshed;
      } catch (brErr: any) {
        console.warn(`[reforecast] branch ${br.runId} failed:`, brErr);
        // The old branch timeline stays; give it back the anchors the rerun lifted.
        brSink?.restore();
        // Clear loading on failure by re-completing with OLD branch values —
        // restores COMPLETED status (clears pulse), result unchanged.
        dispatchRun(
          runCompleted(br.runId, {
            contract: br.contract,
            serverPrediction: br.serverPrediction ?? null,
            events: br.events,
            serverContext: br.serverContext,
            serverExplanation: br.serverExplanation,
            report: br.report,
          }),
        );
        execTable.release(br.runId); // release the claim taken above (failure)
        return br; // keep old branch shape on per-branch failure
      }
    });
    // Now run the MAIN spine. The branch placeholders are already RUNNING and
    // their reruns (branchPromises above) are already in flight — this
    // multi-minute await runs concurrently with them, not before them.
    // Reuse the active contract's identity (question + resolution_criteria +
    // resolution_date) so the forecast server rebuilds the SAME contract hash
    // instead of re-deriving it from the bare question — which dropped
    // resolution_criteria to "Not provided" and RE-KEYED the contract on every
    // reforecast (orphaning its schedule + destroying the criteria). See
    // lib/forecast/reforecastRequest.js. Passed as the preprocessedQuestion
    // (5th) arg, mirroring the fresh-forecast path in useForecastSubmit.js.
    const preprocessed = buildReforecastPreprocessed(active.contract, q, outcomeSpace);
    mainSink = forecastLiveProgressEnabled && fcId ? liveFrameSink(deps, fcId, fcId) : null;
    const fc = await genForecastFromServer(q, forecastEngine, null, false, preprocessed, null, {
      clientId: active.id,
      forecastId: selectRun(store.getState(), active.id)?.forecastId || active.forecastId,
      onProgress: onReforecastProgress,
      // Folds typed frames into the root run; lifts the row's previous-vintage anchors on the
      // first trigger frame (see liveFrameSink; restored in the catch below).
      onUpdate: mainSink ?? undefined,
      onNarration:
        forecastLiveProgressEnabled && fcId
          ? (frame: unknown) => applyNarrationFrame(dispatchRun, store.getState, fcId, frame)
          : undefined,
      onContractKey: (p: any) => {
        recordContractKey(p);
        if (fcId) dispatchRun(contractKeySet(fcId, p.key, p.key_status));
      },
      onTraceAllocated: ({ traceId, forecastId, backend }: any) => {
        // Reforecast reruns an existing run; re-stamp (overwrite) so the store
        // trace tracks the server's rolled-forward lifecycle row and a reload's
        // resume-sweep reattaches the CORRECT vintage (fixes stale timeline/log).
        if (fcId) dispatchRun(traceReallocated(fcId, { traceId, forecastId, backend }));
        // That sweep covers only the viewer's own forecasts; a shared one is reattached by
        // resumeSharedRerun from this handle, after the shared list loads.
        if (fcId && store.getState().sharedRunsById?.[fcId]) writeSharedRerunHandle(fcId, traceId);
      },
      tourFixture: getTourFixture(),
      tourBranchIncrease: getTourBranchIncrease(),
    });
    // The main stream is over: discard any frame the intro still holds so it can't land
    // on the run after its result.
    intro?.stop();
    // mergeReforecastEvents: a reforecast is a full re-run, so fc.events is the
    // complete fresh timeline. We ACCUMULATE the past (keep oldEvents' past
    // triggers + add any newly-occurred ones) and REFRESH the future (only the
    // fresh run's future events), split at nowDate. Deduped + sorted.
    // mergeReforecastEvents imported from lib/forecast/reforecastEvents.js. The merge
    // preserves each event's run id and stamps the stable `occurrenceKey` the live nodes
    // are keyed on, so they reconcile in place at done.
    const mergedEvents = withPremiseOutcomes(
      mergeReforecastEvents({ oldEvents, newEvents: fc.events || [], nowDate }),
      oldEvents,
      selectBranchRuns(store.getState(), active.id),
      active.id,
    );
    // Land the main forecast IMMEDIATELY (with branches flagged
    // _loading:true for the ones we know how to refresh) so the new
    // contract / events / report appear at the same moment the per-
    // branch pulses light up. Without this, the main forecast would
    // appear to "freeze" until every branch's server call finished.
    // Preserve the ORIGINAL contract's question + title across reforecasts.
    // The server re-preprocesses the user's input every call, and minor
    // re-wordings (or LLM stochasticity) can produce a slightly different
    // question_text — which would change the prominent display text on the
    // contract panel + tooltip mid-tracking. The displayed question should
    // stay stable across reforecasts; only the probability + events +
    // report should refresh. Fall back to fc.contract's fields only if
    // the original somehow lacks them (defensive — shouldn't happen).
    // Preserve the OUTCOME SPACE too, for the same reason and with the same care: it is the
    // question's SHAPE, so a rerun that came back with no space (a degraded run, or a server
    // that re-authored it as binary) must not be allowed to erase it — the next reforecast,
    // and any branch created off this contract, would then have nothing to re-send and the
    // forecast would be binary from there on. `withOutcomeSpace` stamps the space ONLY; the
    // distribution itself is never carried forward, so what renders is still exactly what
    // this run returned.
    const preservedContract = withOutcomeSpace(
      {
        ...fc.contract,
        question: active.contract?.question || fc.contract?.question,
        title: active.contract?.title || fc.contract?.title,
      },
      outcomeSpace,
    );
    // Persist the "since last reforecast" probability change so the Forecast
    // Log ticker survives a reload. `move` has no DB column (`history` does), but
    // serverContext round-trips via the server_ctx JSONB column (same pattern
    // as report/resolution) and rowToWire rehydrates .move from it. Suppress a
    // 0pp move (matches the ticker's own 0% suppression); null clears a stale one.
    const _prevProb = active?.contract?.probability;
    const _currProb = preservedContract?.probability;
    const _movePp =
      typeof _prevProb === "number" && typeof _currProb === "number"
        ? Math.round(Math.abs(_currProb - _prevProb) * 100)
        : 0;
    const _reforecastMove =
      _movePp > 0 ? { dir: _currProb > _prevProb ? "up" : "down", val: _movePp } : null;
    // Attach the move to the server context, but keep it NULL when there is
    // neither a base context nor a move to persist — don't fabricate an empty
    // `{move:null}` object (that would turn "no serverContext" into "empty
    // serverContext", churning the DB and contradicting the branch logic that
    // treats {} as absent). When a base context DOES exist, still stamp `move`
    // (even null) so a stale move is cleared.
    // Pick the base context, treating an empty object as absent on EACH
    // candidate (genForecastFromServer normalizes a missing serverContext to
    // {}, which is truthy) — so a server-returned {} falls back to a non-empty
    // prior active.serverContext instead of clobbering it, and a bare
    // `{move:null}` is never persisted when there's genuinely no context.
    const _nonEmptyCtx = (c: any) => (c && Object.keys(c).length > 0 ? c : null);
    const _baseCtx = _nonEmptyCtx(fc.serverContext) || _nonEmptyCtx(active.serverContext) || null;
    const _ctxWithMove =
      _baseCtx || _reforecastMove ? { ...(_baseCtx || {}), move: _reforecastMove } : null;
    const mainStateReforecasted = {
      ...active,
      contract: preservedContract,
      events: mergedEvents,
      // Preserve the OLD structured report (and serverContext / explanation)
      // when the new server response lacks them. Mirrors the per-branch
      // pattern below — without this, a reforecast call to an orchestrator
      // that doesn't emit a structured `report` field (some legacy engines)
      // would null out the report on the live state, causing the executive
      // report modal to fall back to the legacy serverExplanation-parsed
      // narrative even though we have a perfectly good structured report
      // from the prior run.
      report: fc.report || active.report || null,
      serverContext: _ctxWithMove,
      serverExplanation: fc.serverExplanation || active.serverExplanation || null,
      history: [...(active.history || []), oldSnapshot],
      // Use the SAME timestamp captured for oldSnapshot.snapshotAt above
      // so the snapshot's "end" and the new active's "start" align
      // exactly — important for the probability-over-time plot line.
      lastReforecastedAt: reforecastMomentIso,
    };
    reconcileCompletionAnchors(deps, fcId, fcId, mainStateReforecasted.events);
    // Only swap the active view if the reforecasted forecast is STILL the
    // active one — if the user navigated to a different forecast while this
    // request was in flight, don't stomp their selection back. (The
    // forecasts[] list entry is updated by id below regardless, so the new
    // result is never lost — it just doesn't yank the view.)
    dispatchRun(
      runReforecasted(fcId, {
        // dual-write reforecast to the store
        contract: mainStateReforecasted.contract,
        serverPrediction: fc.serverPrediction ?? null,
        events: mainStateReforecasted.events,
        serverContext: mainStateReforecasted.serverContext,
        lastReforecastedAt: reforecastMomentIso,
        history: mainStateReforecasted.history,
        ...(mainStateReforecasted.report !== undefined
          ? { report: mainStateReforecasted.report }
          : {}),
        ...(mainStateReforecasted.serverExplanation !== undefined
          ? { serverExplanation: mainStateReforecasted.serverExplanation }
          : {}),
        obs: fc._obs,
      }),
    );
    saveSharedRerun(deps, fcId, forecastLiveProgressEnabled);
    // Reforecast is user-initiated activity — update the observability panel's
    // "last …" to this rerun (submit/branch/resume already do). Mirrors run.obs.
    if (fc._obs) setLastObs({ ...fc._obs, ts: new Date().toISOString() });
    recordUiEvent({
      event: "ui.forecast.reforecast",
      forecastId: reforecastParentFc,
      newProbability: fc.contract?.probability ?? null,
    });
    // Wait for every branch to either complete or error out.
    // Per-branch state updates already happened inside the map.
    await Promise.all(branchPromises);
    // The pinned `createdAt` + `lastReforecastedAt` semantics live on the
    // mainStateReforecasted object set above — original GENERATED line
    // stays put, the cooldown gate on the REFORECAST button reads
    // `lastReforecastedAt` so spamming the button is still rate-limited
    // to 30-min windows even though createdAt no longer moves.
    // Swap the pending "Reforecasting…" message with a completion line
    // showing the new probability. Filter the pending one out first so a
    // second reforecast can't stack two completion messages.
  } catch (e: any) {
    console.warn("[reforecast] failed:", e);
    if (e?.name !== "AbortError")
      toast("Couldn't update this forecast. Try again in a minute.", { kind: "error" });
    // The old root timeline stays; give it back the anchors the rerun lifted.
    mainSink?.restore();
    // Dispatch terminal error so the run stops spinning if it threw before any progress frame.
    if (fcId) dispatchRun(runErrored(fcId, e));
  } finally {
    intro?.stop(); // clear the paced-intro timers on every exit (success/error)
    // Only clear the shared foreground state if THIS reforecast owned it (see the
    // ownsForeground capture above) — a background reforecast leaves the foreground
    // run's loading/phase alone so it doesn't clobber the run the user is watching.
    if (ownsForeground) {
      setLoading(false);
      setForecastingPhase(null);
    }
    if (claimedMain) execTable.release(fcId);
    // A branch rerun still going after a failed main run keeps the handle for a reload; both
    // slices, since a branch the viewer created on the shared forecast is in their own.
    if (fcId) settleSharedRerunHandle(selectPremiseRunsById(store.getState()), fcId);
  }
  // Omitted deps are stable bindings passed from AppInner — dispatchRun, the
  // runStateRef/execByRunIdRef refs, traceReallocated (a plain action-creator
  // import), the setLoading/setForecastingPhase setters, and the live
  // tour-global getters — so omitting them preserves the original AppInner
  // memoization exactly. The listed entries are the deliberate reactive set;
  // eslint can no longer infer the rest are stable once they're hook arguments.
}
